import { NewProgressBackgroundColors } from "@playlive/overlay-core/progress-bars/new";
import PropTypes from "prop-types";
import NumberSliderInput from "@/components/input/number-slider.input.jsx";
import Picker from "@/components/input/picker.input.jsx";
import YesNoButtonInput from "@/components/input/yes-no-button.input.jsx";
import CustomizeSection from "@/components/overlays/shared/customize-section.jsx";
import LinkSetup from "@/components/overlays/social/link-setup/link-setup.jsx";
// import GroupedColorPicker from "@/components/pickers/grouped-color.picker.jsx";
import ColorPicker from "@/components/pickers/color-picker/color.picker.jsx";
import ColorPickerLabel from "@/components/pickers/color-picker/color-picker-label.jsx";
import CharitySection from "@/components/shared/charity-section.jsx";
import { charityIsTiltify, charityIsTwitch, isYes } from "@/util/common.js";

CustomizeHorizontalProgressBar.propTypes = {
  setConfigState: PropTypes.func.isRequired,
  setConfigKeyValue: PropTypes.func.isRequired,
  showOriginalGoal: PropTypes.bool,
  showGoalAmount: PropTypes.bool,
  overlayURL: PropTypes.string.isRequired,
  overlayName: PropTypes.string.isRequired,
  showCCAM: PropTypes.bool,
  obsDimensions: PropTypes.shape({
    width: PropTypes.string,
    height: PropTypes.string,
  }),
  config: PropTypes.shape({
    charityType: PropTypes.string,
    textColor: PropTypes.string,
    bgColor: PropTypes.string,
    showChallenges: PropTypes.oneOf(["yes", "no"]),
    showRewards: PropTypes.oneOf(["yes", "no"]),
    showDonors: PropTypes.oneOf(["yes", "no"]),
    showScroller: PropTypes.oneOf(["yes", "no"]),
    ccamMode: PropTypes.oneOf(["yes", "no"]),
    showOriginalGoal: PropTypes.oneOf(["yes", "no"]),
    showGoalAmount: PropTypes.oneOf(["yes", "no"]),
  }),
  children: PropTypes.node,
  childrenSlot: PropTypes.oneOf(["top", "bottom", "middle", "scroller", "appearance"]),
};

export default function CustomizeHorizontalProgressBar({
  setConfigState,
  setConfigKeyValue,
  overlayURL,
  config,
  overlayName,
  obsDimensions,
  children,
  childrenSlot = "top",
  showCCAM = new Date().getMonth() === 8, // Show CCAM in September
}) {
  if (
    isYes(config.showScroller) &&
    !isYes(config.showRewards) &&
    !isYes(config.showChallenges) &&
    !isYes(config.showDonors)
  ) {
    setConfigState({ target: { name: "showScroller", value: "no" } });
  }

  // Handles switching from Tiltify -> Twitch so that invalid options aren't shown
  if (charityIsTwitch(config.charityType)) {
    if (isYes(config.showRewards)) {
      setConfigState({ target: { name: "showRewards", value: "no" } });
    }
    if (isYes(config.showChallenges)) {
      setConfigState({ target: { name: "showChallenges", value: "no" } });
    }
  }

  const flagPositions = [
    { name: "Top Left", value: "top,left" },
    { name: "Bottom Left", value: "bottom,left" },
    { name: "Top Right", value: "top,right" },
    { name: "Bottom Right", value: "bottom,right" },
  ];

  const textSize = [
    { name: "Small", value: "small" },
    { name: "Medium", value: "medium" },
    { name: "Large", value: "large" },
  ];

  const themes = [
    { name: "Classic", value: "classic" },
    { name: "Slanted", value: "slanted" },
  ];

  const setFlagPosition = (event) => {
    const [vertical, horizontal] = event.target.value.split(",");
    setConfigKeyValue("ccamFlagVerticalPosition", vertical);
    setConfigKeyValue("ccamFlagHorizontalPosition", horizontal);
  };

  function filterColors(color) {
    return NewProgressBackgroundColors.find((c) => c === color);
  }

  const handleOpacityChange = (e) => {
    const value = e.target.value;
    setConfigKeyValue("backgroundOpacity", value);
  };

  const handleScrollerChange = (event) => {
    const value = event.target.value;
    setConfigState(event);

    if (value === "no") {
      if (isYes(config.showRewards)) {
        setConfigState({ target: { name: "showRewards", value: "no" } });
      }
      if (isYes(config.showChallenges)) {
        setConfigState({ target: { name: "showChallenges", value: "no" } });
      }
      if (isYes(config.showDonors)) {
        setConfigState({ target: { name: "showDonors", value: "no" } });
      }
    } else if (value === "yes") {
      if (
        !isYes(config.showRewards) &&
        !isYes(config.showChallenges) &&
        !isYes(config.showDonors)
      ) {
        if (charityIsTiltify(config.charityType)) {
          setConfigState({ target: { name: "showRewards", value: "yes" } });
          setConfigState({ target: { name: "showChallenges", value: "yes" } });
        }
        setConfigState({ target: { name: "showDonors", value: "yes" } });
      }
    }
  };

  return (
    <div className="mt-0 ml-auto mr-auto mb-4  px-0 w-full">
      <CharitySection onChange={setConfigState} charityType={config.charityType} />

      {childrenSlot === "top" && <div>{children}</div>}

      <CustomizeSection title="Appearance">
        {/* <GroupedColorPicker
          disableBackgroundColor={isYes(config.ccamMode) && showCCAM}
          textColor={config.textColor}
          bgColor={config.bgColor}
          onChange={setConfigState}
          filterBG={filterColors}
        /> */}
        <ColorPickerLabel
          color={config.bgColor}
          labelText="Progress bar"
          className={
            isYes(config.ccamMode)
              ? "opacity-50 transition-opacity"
              : "opacity-100 transition-opacity"
          }
        />
        <ColorPicker
          disabled={isYes(config.ccamMode)}
          currentColor={config.bgColor}
          fieldName="bgColor"
          filterFunction={filterColors}
          onChange={setConfigState}
        />
        <Picker
          onChange={setConfigState}
          currentValue={config.type}
          items={themes}
          fieldName="type"
          labelText="Theme"
        />
        <Picker
          onChange={setConfigState}
          currentValue={config.textSize}
          items={textSize}
          fieldName="textSize"
          labelText="Text Size"
        />

        <div className="my-2">
          <div className="flex flex-col md:flex-row mt-2">
            <YesNoButtonInput
              className="md:mr-4 mb-2 md:mb-0 flex-1"
              onChange={setConfigState}
              mode="checkbox"
              currentValue={config.showGoalAmount}
              name="showGoalAmount"
              label="Show Goal Amount"
              labelTextClassName="text-white"
              hidden={!charityIsTiltify(config.charityType)}
            >
              <p className="pl-1 pt-1 text-base-content">Turn goal visibility on or off</p>
            </YesNoButtonInput>
            <YesNoButtonInput
              className="flex-1"
              onChange={setConfigState}
              mode="checkbox"
              currentValue={config.showOriginalGoal}
              name="showOriginalGoal"
              label="Show Next Goal Progress"
              labelTextClassName="text-white"
              hidden={!charityIsTiltify(config.charityType)}
            >
              <p className="pl-1 pt-1 text-base-content">
                When enabled, the progress bar will celebrate reaching your original goal and
                automatically extend to show progress toward your new goal if it's updated.
              </p>
            </YesNoButtonInput>
          </div>

          <NumberSliderInput
            className="mt-2"
            fieldName="progressBarOpacity"
            maxValue={100}
            minValue={1}
            disabled={config.sound === "no"}
            step={1}
            labelText="Progress Bar Opacity"
            onChange={handleOpacityChange}
            value={config.backgroundOpacity}
            suffix="%"
          />
        </div>

        {childrenSlot === "appearance" && <div>{children}</div>}
      </CustomizeSection>

      {childrenSlot === "middle" && <div>{children}</div>}

      <CustomizeSection title="Scroller" className="mb-4">
        {childrenSlot === "scroller" && <div className="mb-4">{children}</div>}

        <div className="flex flex-col md:flex-row">
          <YesNoButtonInput
            className="md:mr-4 mb-2 md:mb-0 flex-1"
            onChange={handleScrollerChange}
            mode="checkbox"
            currentValue={config.showScroller}
            name="showScroller"
            label="Display Scroller"
            labelTextClassName="text-white"
            hidden={!charityIsTiltify(config.charityType)}
          />

          <YesNoButtonInput
            className="md:mr-4 mb-2 md:mb-0 flex-1"
            onChange={setConfigState}
            mode="checkbox"
            currentValue={config.showRewards}
            disabled={!isYes(config.showScroller)}
            name="showRewards"
            label="Display Rewards"
            labelTextClassName="text-white"
            hidden={!charityIsTiltify(config.charityType)}
          />

          <YesNoButtonInput
            className="md:mr-4 mb-2 md:mb-0 flex-1"
            onChange={setConfigState}
            mode="checkbox"
            currentValue={config.showChallenges}
            disabled={!isYes(config.showScroller)}
            name="showChallenges"
            label="Display Targets"
            hidden={!charityIsTiltify(config.charityType)}
          />

          <YesNoButtonInput
            className="flex-1"
            onChange={setConfigState}
            mode="checkbox"
            currentValue={config.showDonors}
            disabled={!isYes(config.showScroller)}
            name="showDonors"
            label="Display Donors"
          />
        </div>
      </CustomizeSection>

      {showCCAM && (
        <CustomizeSection title="CCAM" className="mb-4">
          <div id="ccam">
            <YesNoButtonInput
              className="flex-1"
              onChange={setConfigState}
              mode="checkbox"
              currentValue={config.ccamMode}
              name="ccamMode"
              label="CCAM Mode"
              labelNoClass="text-black"
              buttonNoClass="bg-warning opacity-60 shadow-none"
              buttonYesClass="bg-warning opacity-100 shadow-none"
              labelYesClass="text-black"
            />

            <Picker
              disabled={!isYes(config.ccamMode)}
              onChange={setFlagPosition}
              currentValue={`${config.ccamFlagVerticalPosition},${config.ccamFlagHorizontalPosition}`}
              items={flagPositions}
              fieldName="ccamFlagPosition"
              labelText="CCAM Ribbon Position"
            />
          </div>
        </CustomizeSection>
      )}

      {childrenSlot === "bottom" && <div>{children}</div>}

      <LinkSetup
        allowTeamCampaignSelection={true}
        charityType={config.charityType}
        obsDimensions={obsDimensions}
        overlayName={overlayName}
        overlayURL={overlayURL}
      />
    </div>
  );
}
